import { useNavigate } from '@tanstack/react-router';
import { Button, RadioButton } from '@yembo/yemblocks-core';
import { toInternationalPhoneNumber } from '@yembo/yemblocks-core/dist/_util';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { MfaEmployee, putSendMfa } from 'src/queries/Login';
import { useSubmitOnEnterKey } from 'src/react/_hooks/useEnterKeySubmit';
import { LoginFormData } from '..';
import { getInitialResendChannel, getSendableMfaChannels } from '../_util/mfaChannels';
import { SectionHeader } from '../SectionHeader';

type Props = {
  formData: LoginFormData;
  setFormData: Dispatch<SetStateAction<LoginFormData>>;
};

type ResendMfaInternalProps = Props & {
  mfaEmployee: MfaEmployee;
};

export const ResendMfa = (props: Props) => {
  const { mfaEmployee } = props.formData;

  useEffect(() => {
    if (!mfaEmployee) {
      window.location.assign('/');
    }
  }, [mfaEmployee]);

  if (!mfaEmployee) return null;

  return <ResendMfaInternal {...props} mfaEmployee={mfaEmployee} />;
};

const ResendMfaInternal = ({ formData, setFormData, mfaEmployee }: ResendMfaInternalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const { availableMfaChannels } = mfaEmployee;
  const sendableChannels = getSendableMfaChannels(availableMfaChannels);
  const hasAuthenticatorApp = availableMfaChannels.includes('totp');

  // The authenticator app is never "resent", so the screen starts on a channel
  // that can be, and keeps its own choice until a code actually goes out.
  const [selectedMfaChannel, setSelectedMfaChannel] = useState(() =>
    getInitialResendChannel(formData.mfaChannel ?? mfaEmployee.preferredMfaChannel, availableMfaChannels)
  );

  const onResend = useCallback(async () => {
    if (!selectedMfaChannel) return;

    setIsLoading(true);

    try {
      await putSendMfa({
        email: formData.email ?? '',
        password: formData.password ?? '',
        channel: selectedMfaChannel,
      });

      setFormData((data) => ({ ...data, mfaChannel: selectedMfaChannel }));
      // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
      navigate({ to: '/verify-mfa', search: true } as any);
    } catch {
      // putSendMfa already surfaced the server's message, e.g. the resend throttle.
    } finally {
      setIsLoading(false);
    }
  }, [formData.email, formData.password, navigate, selectedMfaChannel, setFormData]);

  const onUseAuthenticatorApp = () => {
    setFormData((data) => ({ ...data, mfaChannel: 'totp' }));
    // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
    navigate({ to: '/verify-mfa', search: true } as any);
  };

  useSubmitOnEnterKey({ onSubmit: onResend });

  return (
    <div className='resend-mfa authentication-form'>
      <SectionHeader heading='Resend code' caption='How would you like to receive your one-time code?' />
      <div className='send-code-options'>
        {sendableChannels.map((channel) => {
          switch (channel) {
            case 'email':
              return (
                <RadioButton
                  key='email'
                  label={
                    <div>
                      <p>Email code to:</p>
                      <p className='bold'>{mfaEmployee.email}</p>
                    </div>
                  }
                  value='email'
                  checked={selectedMfaChannel === 'email'}
                  onChange={() => setSelectedMfaChannel('email')}
                />
              );
            case 'sms':
              return (
                <RadioButton
                  key='sms'
                  label={
                    <div>
                      <p>Text code to:</p>
                      <p className='bold'>{mfaEmployee.phone ? toInternationalPhoneNumber(mfaEmployee.phone) : ''}</p>
                    </div>
                  }
                  value='sms'
                  checked={selectedMfaChannel === 'sms'}
                  onChange={() => setSelectedMfaChannel('sms')}
                />
              );
          }
        })}
      </div>
      <div className='buttons-wrapper'>
        <Button text='Resend' isLoading={isLoading} onClick={onResend} isDisabled={!selectedMfaChannel} />
        {hasAuthenticatorApp && (
          <Button text='Use authenticator app' kind='outline' onClick={onUseAuthenticatorApp} isDisabled={isLoading} />
        )}
        <Button
          text='Use backup code'
          kind='outline'
          // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
          onClick={() => navigate({ to: '/verify-backup-code', search: true } as any)}
          isDisabled={isLoading}
        />
      </div>
    </div>
  );
};
